import React from 'react';
import { PendingUser } from '@/schemas/admin.types';
import { formatDate } from '@/components/ui/styles/adminHelpers';
import Table from '@/components/ui/Table'; 
import UserAvatar from '../ui/UserAvatar';
import RoleBadge from '../ui/RoleBadge';
import ActionButtons from '../ui/ActionButtons';
import EmptyState from '../ui/EmptyState';

interface PendingUsersTableProps {
  users: PendingUser[];
  onApprove: (user: PendingUser) => Promise<void>;
  onReject: (user: PendingUser) => Promise<void>;
  userVideoCountMap?: Map<string, number>;
}

/**
 * PendingUsersTable - Table component for displaying pending user approvals
 * 
 * Displays a comprehensive list of users awaiting admin approval with interactive actions.
 * Features include user avatars, role badges, request timestamps, and approval controls.
 * 
 * Features:
 * - User avatar with initials fallback
 * - Color-coded role badges
 * - Request timestamps and numbering
 * - Approve/Reject action buttons
 * - Empty state handling
 * - Responsive design
 * 
 * @param users - Array of pending users awaiting approval
 * @param onApprove - Async function to approve a user
 * @param onReject - Async function to reject a user
 */
const PendingUsersTable: React.FC<PendingUsersTableProps> = ({
  users,
  onApprove,
  onReject,
  userVideoCountMap
}) => {
  /**
   * Renders user information with avatar and details
   */
  const renderUserCell = (_: any, user: PendingUser) => (
    <div className="flex items-center">
      <UserAvatar 
        name={user.name} 
        email={user.email} 
        size="lg" 
        showGradient={true}
        className="flex-shrink-0"
      />
      <div className="ml-4">
        <div className="text-sm font-medium text-gray-900 text-left">
          {user.name || 'Unknown User'}
        </div>
        <div className="text-sm text-gray-500 truncate max-w-48" title={user.email}>
          {user.email}
        </div>
      </div>
    </div>
  );

  /**
   * Renders role badge with appropriate styling
   */
  const renderRoleCell = (_: any, user: PendingUser) => (
    <RoleBadge role={user.role} />
  );

  /**
   * Renders video count for the user
   */
  const renderVideoCountCell = (_: any, user: PendingUser) => (
    <div className="flex items-center text-sm text-gray-900">
      <svg className="w-4 h-4 mr-1.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
      </svg>
      <span>{userVideoCountMap?.get(user.email) || 0}</span>
    </div>
  );

  /**
   * Renders request date and metadata
   */
  const renderDateCell = (_: any, user: PendingUser) => (
    <div>
      <div className="text-sm text-gray-900">{formatDate(user.created_at)}</div>
      <div className="text-xs text-gray-500">Request #{users.indexOf(user) + 1}</div>
    </div>
  );

  /**
   * Renders action buttons for approve/reject
   */
  const renderActionsCell = (_: any, user: PendingUser) => (
    <ActionButtons 
      showApproveReject={true}
      onApprove={() => onApprove(user)}
      onReject={() => onReject(user)}
    />
  );

  // Table column configuration
  const columns = [
    {
      key: 'user',
      label: 'User',
      render: renderUserCell
    },
    {
      key: 'role',
      label: 'Role',
      render: renderRoleCell
    },
    {
      key: 'videos',
      label: 'Videos',
      render: renderVideoCountCell
    },
    {
      key: 'created_at',
      label: 'Requested',
      render: renderDateCell
    },
    {
      key: 'actions',
      label: 'Actions',
      render: renderActionsCell
    }
  ];

  return (
    <div className="bg-white shadow rounded-lg">
      <Table
        data={users}
        columns={columns}
        emptyMessage="All user requests have been processed"
        emptyIcon={
          <EmptyState 
            icon={
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            }
            title=""
            description=""
          />
        }
      />
    </div>
  );
};

export default PendingUsersTable;
